import { useEffect } from "react";
import { useAppStore } from "../store/useAppStore";

function clamp01(value: number) {
  return Math.min(Math.max(value, 0), 1);
}

function smoothstep(value: number) {
  const progress = clamp01(value);
  return progress * progress * (3 - 2 * progress);
}

function getVelocityDuration(scrollVelocity: number, minDuration: number, maxDuration: number) {
  const speed = Math.abs(scrollVelocity);
  const normalizedSpeed = smoothstep((speed - 0.08) / 1.55);
  return maxDuration - (maxDuration - minDuration) * normalizedSpeed;
}

export function useSectionTransition() {
  const currentSection = useAppStore((state) => state.currentSection);
  const targetSection = useAppStore((state) => state.targetSection);
  const isTransitioning = useAppStore((state) => state.isTransitioning);
  const reducedMotion = useAppStore((state) => state.reducedMotion);
  const setTransitionProgress = useAppStore((state) => state.setTransitionProgress);
  const completeTransition = useAppStore((state) => state.completeTransition);

  useEffect(() => {
    if (!isTransitioning || currentSection === targetSection) {
      return;
    }

    const transitionState = { progress: 0 };
    let active = true;
    let tween: { kill: () => void } | null = null;

    void import("gsap").then(({ default: gsap }) => {
      if (!active) {
        return;
      }

      const isStudioProjectsTransition = currentSection === "studio" && targetSection === "projects" && !reducedMotion;
      const scrollVelocity = useAppStore.getState().scrollVelocity;

      if (isStudioProjectsTransition) {
        tween = gsap.to(transitionState, {
          duration: getVelocityDuration(scrollVelocity, 1, 2),
          ease: "sine.out",
          progress: 1,
          onUpdate: () => {
            setTransitionProgress(transitionState.progress);
          },
          onComplete: () => {
            completeTransition();
          }
        });
        return;
      }

      tween = gsap.to(transitionState, {
        duration: reducedMotion ? 0.8 : getVelocityDuration(scrollVelocity, 1, 2),
        ease: "sine.out",
        progress: 1,
        onUpdate: () => {
          setTransitionProgress(transitionState.progress);
        },
        onComplete: () => {
          completeTransition();
        }
      });
    });

    return () => {
      active = false;
      tween?.kill();
    };
  }, [completeTransition, currentSection, isTransitioning, reducedMotion, setTransitionProgress, targetSection]);
}
